/**
 * Each linked group's day, for the Today grid on its card (19/10/2026): the
 * container's drops that send to it today, in time order (their Danish
 * time; a rank when the drop is tied to one), and today's lists by name
 * when the plan knows them. A drop the window or the end time cuts is not a
 * drop of today. See group-days.ts.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMemo } from 'react';

import type { ContainerDraft, DropGenPlanDetail } from '../../../../drop-generator.types';
import { useDayPreview } from '../../../../useDropGenerator';
import { dkTime } from '../../../../utils/clock/dk-label';
import { minutesLabel } from '../../../../utils/format';
import { slotKey } from '../../../../utils/slot-times';
import { useCountryClock } from '../../../shared/time/country-clock-context';
import { useGeneralTimes } from '../../general/hooks/use-general-times';
import { previewOf } from '../../general/slots/list/cells/preview-of';
import { type GroupDay, groupDay, goesToday, type TimedSlot } from './group-days';

export function useGroupDays(draft: ContainerDraft, plan: DropGenPlanDetail, containerId: string | null): Map<string, GroupDay> {
  const { timesByKey } = useGeneralTimes(draft, plan, containerId);
  const { tz, runDate } = useCountryClock();
  const preview = useDayPreview(plan.countryKey);
  return useMemo(() => {
    const rows = previewOf(preview.data?.containers ?? [], draft.slots, containerId).groups;
    const timed = draft.slots
      .map((slot, i) => ({ slot, time: timesByKey.get(slotKey(slot, i)) }))
      .filter((r): r is TimedSlot => r.slot.enabled && goesToday(r.time))
      .sort((a, b) => a.time.minute - b.time.minute);
    const label = (min: number) => dkTime(min, tz, runDate)?.label ?? minutesLabel(min);
    return new Map(draft.groups.map((g) => [g.listGroupId, groupDay(g.listGroupId, timed, label, rows.find((r) => r.listGroupId === g.listGroupId))]));
  }, [draft.slots, draft.groups, timesByKey, tz, runDate, preview.data, containerId]);
}
